import { UntitledIcon } from '@faceup/icons'
import { ulDownload01 } from '@faceup/icons/ulDownload01'
import { ulXCircle } from '@faceup/icons/ulXCircle'
import { Tooltip } from '@faceup/ui-base'
import {
  type ComponentProps,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ucLoader, ucPauseButton, ucPlayButton } from '../customIcons'
import { UiContext } from '../UiProvider/UiProvider'
import { UnstyledButton } from '../UnstyledButton'

type AudioPlayerWithRemoveProps = {
  onRemove?: () => Promise<void>
  onDownload?: never
}

type AudioPlayerWithDownloadProps = {
  onRemove?: never
  onDownload?: () => Promise<void>
}

export type AudioPlayerProps = {
  duration?: string
  onPlay?: () => Promise<string>
  label?: ReactNode
} & (AudioPlayerWithRemoveProps | AudioPlayerWithDownloadProps)

export const AudioPlayer = ({
  duration,
  onPlay,
  onRemove,
  onDownload,
  label,
}: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPlaybackLoading, setIsPlaybackLoading] = useState(false)
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [isRemoveLoading, setIsRemoveLoading] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [totalDuration, setTotalDuration] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressBarRef = useRef<HTMLDivElement | null>(null)
  const { audioPlayer } = useContext(UiContext)
  const translations = audioPlayer ?? {
    label: 'Audio recording',
    play: 'Play',
    pause: 'Pause',
    playDisabled: 'Playback not available',
    download: 'Download',
    remove: 'Remove',
  }

  const formatTime = (seconds: number): string => {
    if (!Number.isFinite(seconds)) {
      return '0:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const updateTime = useMemo(
    () => (clientX: number) => {
      const audio = audioRef.current
      const progressBar = progressBarRef.current
      if (!audio || !totalDuration || !progressBar) {
        return
      }

      const rect = progressBar.getBoundingClientRect()
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
      const percentage = x / rect.width
      const newTime = percentage * totalDuration
      audio.currentTime = newTime
      setCurrentTime(newTime)
    },
    [totalDuration]
  )

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true)
    updateTime(e.clientX)
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        updateTime(e.clientX)
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }

    return
  }, [isDragging, updateTime])

  const handlePlayback = async () => {
    const audio = audioRef.current

    if (!audio) {
      return
    }

    if (onPlay && !audio.src) {
      setIsPlaybackLoading(true)
      try {
        const newSource = await onPlay()
        audio.src = newSource
      } finally {
        setIsPlaybackLoading(false)
      }
    }

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      try {
        await audio.play()
        setIsPlaying(true)
      } catch (error) {
        setIsPlaying(false)
        console.error('Error playing audio:', error)
        // download instead if playback fails
        handleDownload()
      }
    }
  }

  const handleDownload = async () => {
    if (!onDownload) {
      return
    }
    setIsDownloadLoading(true)
    try {
      await onDownload()
    } finally {
      setIsDownloadLoading(false)
    }
  }

  const handleRemove = async () => {
    if (!onRemove) {
      return
    }
    setIsRemoveLoading(true)
    try {
      await onRemove()
    } finally {
      setIsRemoveLoading(false)
    }
  }

  return (
    <div
      className={
        'flex items-center gap-3 w-fit p-2 pr-3 rounded-full bg-colorPrimaryBg hover:bg-colorPrimaryBgHover transition-colors'
      }
    >
      <div className='flex gap-2 items-center grow'>
        <audio
          ref={audioRef}
          onEnded={() => setIsPlaying(false)}
          onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
          onLoadedMetadata={e => setTotalDuration(e.currentTarget.duration)}
        />
        <IconButtonWithTooltip
          label={
            !onPlay ? translations.playDisabled : isPlaying ? translations.pause : translations.play
          }
          onClick={handlePlayback}
          disabled={!onPlay}
        >
          <UntitledIcon
            icon={isPlaybackLoading ? ucLoader : isPlaying ? ucPauseButton : ucPlayButton}
            size={20}
            isSpinning={isPlaybackLoading}
          />
        </IconButtonWithTooltip>
        <div className='font-semibold text-sm text-colorPrimary truncate max-w-[200px]'>
          {label ?? translations.label}
        </div>
      </div>

      <div className='flex gap-2 items-center grow-0'>
        {totalDuration > 0 && (
          <div className='flex items-center gap-2'>
            <span className='whitespace-nowrap text-xs font-medium text-colorTextSecondary tabular-nums'>
              {formatTime(currentTime)}
            </span>
            <div
              ref={progressBarRef}
              className='w-24 h-1 bg-colorBorder rounded-full cursor-pointer hover:h-2 transition-all group select-none'
              onMouseDown={handleMouseDown}
            >
              <div
                className='h-full bg-colorPrimary rounded-full group-hover:bg-colorPrimaryHover transition-colors'
                style={{ width: `${(currentTime / totalDuration) * 100}%` }}
              />
            </div>
            <span className='whitespace-nowrap text-xs font-medium text-colorTextSecondary tabular-nums'>
              {duration ?? formatTime(totalDuration)}
            </span>
          </div>
        )}
        {totalDuration === 0 && duration && (
          <span className='text-xs font-medium text-colorTextSecondary'>{duration}</span>
        )}

        {onDownload && (
          <IconButtonWithTooltip
            label={translations.download}
            onClick={handleDownload}
            disabled={isDownloadLoading}
          >
            <UntitledIcon
              icon={isDownloadLoading ? ucLoader : ulDownload01}
              size={12}
              isSpinning={isDownloadLoading}
            />
          </IconButtonWithTooltip>
        )}

        {onRemove && (
          <IconButtonWithTooltip label={translations.remove} onClick={handleRemove}>
            <UntitledIcon
              icon={isRemoveLoading ? ucLoader : ulXCircle}
              size={12}
              isSpinning={isRemoveLoading}
            />
          </IconButtonWithTooltip>
        )}
      </div>
    </div>
  )
}

type IconButtonWithTooltipProps = ComponentProps<typeof UnstyledButton> & {
  label: string
}

const IconButtonWithTooltip = ({ label, ...props }: IconButtonWithTooltipProps) => {
  return (
    <Tooltip title={label}>
      <UnstyledButton
        className='leading-0 transition-colors text-colorPrimary hover:text-colorPrimaryHover disabled:opacity-50 disabled:cursor-not-allowed'
        {...props}
      />
    </Tooltip>
  )
}
